import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import {
  DEFAULT_INVOICES_FILTERS,
  DEFAULT_INVOICES_SUMMARY,
  type InvoicesListFilters,
  type InvoicesState
} from "../types/invoices.types.ts"
import {
  billableLinesArgKind,
  type FetchBillableLinesArg,
  exportInvoicesList,
  fetchBillableLines,
  fetchInvoicesList,
  fetchInvoicesSummary,
  generateInvoice,
  issueInvoice,
  patchGeneratedInvoice,
  sendInvoice,
  sliceName,
  voidInvoice
} from "./invoices.thunks.ts"

const DEFAULT_INVOICES_ORDERING = "-date_issued"

type BillableAction = {
  meta?: { requestId?: string; arg?: FetchBillableLinesArg }
}

// An answer older than the newest request is dropped; one dispatched without
// request metadata (a test, a seed) is taken as it is.
const isStaleBillableAnswer = (latest: string | null, action: BillableAction) =>
  !!latest && !!action.meta?.requestId && action.meta.requestId !== latest

const billableKindOf = (action: BillableAction) =>
  action.meta?.arg != null
    ? billableLinesArgKind(action.meta.arg)
    : "receivable"

const initialState: InvoicesState = {
  items: [],
  count: 0,
  next: null,
  retryNext: null,
  fetching: false,
  requestId: null,
  exporting: false,
  filters: DEFAULT_INVOICES_FILTERS,
  ordering: DEFAULT_INVOICES_ORDERING,
  billableLines: [],
  billableDefaults: {},
  billableLinesFetching: false,
  billableLinesKind: null,
  billableLinesRequestId: null,
  saving: false,
  summary: DEFAULT_INVOICES_SUMMARY,
  summaryFetching: false,
  summaryFailed: false
}

const invoicesSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    invoicesSetFilters: (
      state,
      action: PayloadAction<Partial<InvoicesListFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    invoicesResetFilters: (state) => {
      state.filters = DEFAULT_INVOICES_FILTERS
      state.ordering = DEFAULT_INVOICES_ORDERING
    },
    invoicesSetOrdering: (state, action: PayloadAction<string | null>) => {
      state.ordering = action.payload
    },
    invoicesClearBillableLines: (state) => {
      state.billableLines = []
      state.billableDefaults = {}
    }
  },
  extraReducers: (builder) => {
    builder
      // The three states of a scrolled list, spelled out here rather than
      // through `redux/shared/paged-list` because this slice keeps its rows and
      // its count in separate fields. Same rule.
      .addCase(fetchInvoicesList.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId

        if (!action.meta?.arg?.isMore) {
          state.items = []
          state.next = null
          state.retryNext = null
        }
      })
      .addCase(fetchInvoicesList.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.items = action.meta?.arg?.isMore
          ? [...state.items, ...action.payload.results]
          : action.payload.results
        state.next = action.payload.next ?? null
        state.retryNext = null
        state.count = action.payload.count
      })
      .addCase(fetchInvoicesList.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null

        // Keep the pages already scrolled in, but stop offering the one that
        // failed — the scroll hook re-fires on every settle while `next` stands.
        // The url moves to `retryNext`, which the footer offers the user. A
        // retry is built from `retryNext`, so on a second failure `next` is
        // already null and the url has to be kept rather than re-read.
        if (action.meta?.arg?.isMore)
          state.retryNext = state.next ?? state.retryNext ?? null
        state.next = null
        if (!action.meta?.arg?.isMore) state.items = []
      })
      .addCase(fetchInvoicesSummary.pending, (state) => {
        state.summaryFetching = true
      })
      .addCase(fetchInvoicesSummary.fulfilled, (state, action) => {
        state.summaryFetching = false
        state.summaryFailed = false
        state.summary = action.payload
      })
      .addCase(fetchInvoicesSummary.rejected, (state) => {
        state.summaryFetching = false
        state.summaryFailed = true
        state.summary = DEFAULT_INVOICES_SUMMARY
      })
      .addCase(exportInvoicesList.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportInvoicesList.fulfilled, (state) => {
        state.exporting = false
      })
      .addCase(exportInvoicesList.rejected, (state) => {
        state.exporting = false
      })
      .addCase(fetchBillableLines.pending, (state, action) => {
        state.billableLinesFetching = true
        state.billableLinesRequestId = action.meta.requestId
      })
      .addCase(fetchBillableLines.fulfilled, (state, action) => {
        // Only the newest request is kept: switching an invoice's type must
        // not leave the previous type's lines on screen when they answer last.
        if (isStaleBillableAnswer(state.billableLinesRequestId, action)) return
        state.billableLinesFetching = false
        state.billableLines = action.payload.lines
        state.billableLinesKind = billableKindOf(action)
        state.billableDefaults = {
          default_tax_pct: action.payload.default_tax_pct,
          default_retainage_pct: action.payload.default_retainage_pct
        }
      })
      .addCase(fetchBillableLines.rejected, (state, action) => {
        if (isStaleBillableAnswer(state.billableLinesRequestId, action)) return
        state.billableLinesFetching = false
        state.billableLinesKind = billableKindOf(action)
        state.billableLines = []
        state.billableDefaults = {}
      })
      .addCase(generateInvoice.pending, (state) => {
        state.saving = true
      })
      .addCase(generateInvoice.fulfilled, (state) => {
        state.saving = false
      })
      .addCase(generateInvoice.rejected, (state) => {
        state.saving = false
      })
      .addCase(patchGeneratedInvoice.pending, (state) => {
        state.saving = true
      })
      .addCase(patchGeneratedInvoice.fulfilled, (state, action) => {
        state.saving = false
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        )
      })
      .addCase(patchGeneratedInvoice.rejected, (state) => {
        state.saving = false
      })
      .addCase(issueInvoice.pending, (state) => {
        state.saving = true
      })
      .addCase(issueInvoice.fulfilled, (state, action) => {
        state.saving = false
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        )
      })
      .addCase(issueInvoice.rejected, (state) => {
        state.saving = false
      })
      .addCase(voidInvoice.fulfilled, (state, action) => {
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        )
      })
      .addCase(sendInvoice.pending, (state) => {
        state.saving = true
      })
      .addCase(sendInvoice.fulfilled, (state, action) => {
        state.saving = false
        state.items = state.items.map((item) =>
          item.id === action.payload.invoice.id
            ? { ...item, ...action.payload.invoice }
            : item
        )
      })
      .addCase(sendInvoice.rejected, (state) => {
        state.saving = false
      })
  }
})

export const {
  invoicesSetFilters,
  invoicesResetFilters,
  invoicesSetOrdering,
  invoicesClearBillableLines
} = invoicesSlice.actions

export default invoicesSlice
